import { notifyError } from "@/lib/notify-error";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import {
  Columns2,
  Eye,
  EyeOff,
  Edit,
  FileText,
  Loader2,
  Maximize2,
  MessageSquare,
  MousePointer2,
  PanelRightClose,
  PanelRightOpen,
  Save,
  Search,
  Square,
} from "lucide-react";
import { Layer, Line, Rect, Stage } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { ViewerBackButton, ViewerTopBar } from "@/components/layout";
import { ViewerMetaChip } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Toggle } from "@/components/ui/toggle";
import {
  ViewportContextMenu,
  ViewportContextMenuItem,
} from "@/components/ui/viewport-context-menu";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import {
  DocumentViewportPageControls,
  DocumentViewportToolbar,
  DocumentViewportToolbarSection,
} from "@/features/document-viewport/toolbar";
import { useDocumentViewport } from "@/features/document-viewport/context";
import type { DocumentSource, ViewportCamera, ViewportRect } from "@/features/document-viewport/types";
import { clampPanToDocumentBounds, getRenderScaleForZoom } from "@/features/document-viewport/utils";
import { CompactTradeSelector } from "@/features/rev-pilot/change-group-notes";
import { cn } from "@/lib/utils";
import { useViewerExit } from "@/components/layout/use-viewer-exit";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

type ComparisonDetails = {
  comparison: {
    _id: Id<"revPilotScheduleComparisons">;
    name: string;
    reportPdfStorageId?: Id<"_storage">;
    reportPdfFileKey?: string;
  };
  project?: { name: string } | null;
  v1Document?: ReviewDocument | null;
  v2Document?: ReviewDocument | null;
  documentPairs?: ReviewDocumentPair[];
};

type ReviewDocument = {
  _id: Id<"revPilotDocuments">;
  originalFilename?: string;
  fileStorageId?: Id<"_storage">;
  fileStorageKey?: string;
};

type ReviewDocumentPair = {
  _id: Id<"revPilotDocumentPairs">;
  displayName: string;
  v1Document?: ReviewDocument | null;
  v2Document?: ReviewDocument | null;
};

type ChangeItem = {
  _id: Id<"revPilotChanges">;
  pageNumber: number;
  changeOrder: number;
  regionId: string;
  included: boolean;
  normalizedBbox: ViewportRect;
  regularizedBbox?: ViewportRect;
  contourPoints?: number[][];
};

type ChangeNote = {
  _id: Id<"revPilotChangeNotes">;
  changeIds: Id<"revPilotChanges">[];
  body: string;
  label: string;
};

type ReviewMode = "single" | "side-by-side";

type NoteFocusRequest = {
  nonce: number;
  pageNumber: number;
  regions: ViewportRect[];
};

type ResizeHandlePosition = "nw" | "ne" | "sw" | "se";

type RegularizedRects = Record<string, ViewportRect>;

const DEFAULT_REVIEW_CAMERA: ViewportCamera = {
  zoom: 1,
  pan: { x: 0, y: 0 },
  fitMode: "fit-width",
  renderScale: 1,
};

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName);
}

function useSpacePanMode() {
  const [spacePanActive, setSpacePanActive] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" || isEditableTarget(event.target)) return;
      event.preventDefault();
      setSpacePanActive(true);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") setSpacePanActive(false);
    };

    const handleWindowBlur = () => setSpacePanActive(false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  return spacePanActive;
}

function ChangeSelectionMenu({
  point,
  selectedCount,
  showExclude,
  showInclude,
  disabled,
  onExclude,
  onInclude,
  onAddNote,
  onRegularize,
  onClose,
}: {
  point: { x: number; y: number } | null;
  selectedCount: number;
  showExclude: boolean;
  showInclude: boolean;
  disabled?: boolean;
  onExclude: () => void;
  onInclude: () => void;
  onAddNote: () => void;
  onRegularize: () => void;
  onClose: () => void;
}) {
  // Every selection action (exclude, include, note, regularize) writes, so reviewers get no menu.
  const canEdit = useCanEdit();
  if (!canEdit || !point || selectedCount === 0) return null;

  return (
    <ViewportContextMenu
      data-viewport-tools-ui
      x={point.x}
      y={point.y}
      onClose={onClose}
      className="w-44"
      aria-label={`Actions for ${selectedCount} selected ${selectedCount === 1 ? "change" : "changes"}`}
    >
      <DropdownMenuLabel className="px-2 py-1 text-xs">{selectedCount} selected</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {showExclude ? (
        <ViewportContextMenuItem icon={<EyeOff />} disabled={disabled} onSelect={onExclude}>
          Hide / exclude
        </ViewportContextMenuItem>
      ) : null}
      {showInclude ? (
        <ViewportContextMenuItem icon={<Eye />} disabled={disabled} onSelect={onInclude}>
          Include selected
        </ViewportContextMenuItem>
      ) : null}
      <ViewportContextMenuItem icon={<MessageSquare />} onSelect={onAddNote}>
        Add note
      </ViewportContextMenuItem>
      <ViewportContextMenuItem icon={<Square />} onSelect={onRegularize}>
        Regularize
      </ViewportContextMenuItem>
    </ViewportContextMenu>
  );
}

const REVIEW_LAYOUT_OPTIONS = [
  { value: "single", label: "Single", icon: Maximize2 },
  { value: "side-by-side", label: "Side-by-side", icon: Columns2 },
] as const satisfies ReadonlyArray<{ value: ReviewMode; label: string; icon: typeof Maximize2 }>;

function ReviewWorkspaceControls({
  comparisonName,
  documentPairs,
  selectedDocumentPairId,
  onDocumentPairChange,
  showExcluded,
  onShowExcludedChange,
  mode,
  onModeChange,
  commentsOpen,
  onCommentsOpenChange,
  selectedCount,
  completedMode,
  isCompleting,
  onBack,
  onCompleteReview,
}: {
  comparisonName: string;
  documentPairs: ReviewDocumentPair[];
  selectedDocumentPairId: string;
  onDocumentPairChange: (value: string) => void;
  showExcluded: boolean;
  onShowExcludedChange: (value: boolean) => void;
  mode: ReviewMode;
  onModeChange: (mode: ReviewMode) => void;
  commentsOpen: boolean;
  onCommentsOpenChange: (open: boolean) => void;
  selectedCount: number;
  completedMode: boolean;
  isCompleting: boolean;
  onBack: () => void;
  onCompleteReview: () => void;
}) {
  // Completing a review is a project write.
  const canEdit = useCanEdit();
  return (
    <ViewerTopBar
      className="absolute inset-x-0 top-0 z-[70] shadow-sm"
      back={
        <ViewerBackButton
          label={completedMode ? "Back to Summary" : "Back"}
          onBack={onBack}
        />
      }
      title={comparisonName}
      subtitle="Review Changes"
      readOnly={!canEdit}
      source={
        documentPairs.length > 0 ? (
          <Select value={selectedDocumentPairId} onValueChange={onDocumentPairChange}>
            <SelectTrigger
              className="h-8 w-44 shrink-0 bg-background text-xs"
              aria-label="Document pair"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {documentPairs.map((pair) => (
                <SelectItem key={pair._id} value={String(pair._id)}>{pair.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null
      }
      meta={
        <ViewerMetaChip className="shrink-0 tabular-nums">
          <MousePointer2 aria-hidden="true" />
          {selectedCount} Selected
        </ViewerMetaChip>
      }
      tools={
        <>
          <SegmentedControl
            aria-label="Review layout"
            size="sm"
            value={mode}
            onValueChange={onModeChange}
            options={REVIEW_LAYOUT_OPTIONS}
          />
          <Toggle
            variant="outline"
            size="sm"
            className="shrink-0 px-2.5 text-xs"
            pressed={showExcluded}
            onPressedChange={onShowExcludedChange}
            aria-label="Show excluded annotations"
          >
            {showExcluded ? <Eye /> : <EyeOff />}
            Excluded
          </Toggle>
          <Toggle
            variant="outline"
            size="sm"
            className="shrink-0 px-2.5 text-xs"
            pressed={commentsOpen}
            onPressedChange={onCommentsOpenChange}
          >
            {commentsOpen ? <PanelRightClose /> : <PanelRightOpen />}
            Narrative Notes
          </Toggle>
        </>
      }
      actions={
        canEdit ? (
          <Button type="button" size="sm" onClick={onCompleteReview} disabled={isCompleting}>
            {isCompleting ? <Loader2 className="animate-spin" /> : null}
            {isCompleting ? "Saving…" : "Complete"}
          </Button>
        ) : null
      }
    />
  );
}

function CommentsSideDrawer({
  open,
  onOpenChange,
  notes,
  selectedChanges,
  changesById,
  selectedCount,
  onAddNote,
  onFocusNote,
  onUpdateNote,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notes: ChangeNote[];
  selectedChanges: ChangeItem[];
  changesById: Map<string, ChangeItem>;
  selectedCount: number;
  onAddNote: () => void;
  onFocusNote: (note: ChangeNote) => void;
  onUpdateNote: (noteId: Id<"revPilotChangeNotes">, body: string, label: string) => Promise<void>;
}) {
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [editingLabel, setEditingLabel] = useState("General");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const canEdit = useCanEdit();
  useOnEditAccessLost(() => setEditingNoteId(null));

  function startEditing(note: ChangeNote) {
    setEditingNoteId(String(note._id));
    setEditingBody(note.body);
    setEditingLabel(note.label);
  }

  async function saveEditing(note: ChangeNote) {
    setIsSavingEdit(true);
    try {
      await onUpdateNote(note._id, editingBody, editingLabel);
      setEditingNoteId(null);
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to update note");
    } finally {
      setIsSavingEdit(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="absolute right-4 top-28 z-40 flex items-center gap-2 rounded-lg border-2 bg-background/95 px-3 py-2 text-sm font-semibold shadow-lg backdrop-blur transition-colors hover:bg-muted"
        onClick={() => onOpenChange(true)}
      >
        <PanelRightOpen className="size-4" />
        Narrative Notes
      </button>
    );
  }

  const selectedNoteIds = new Set(
    selectedChanges.flatMap((change) => notes.filter((note) => note.changeIds.map(String).includes(String(change._id))).map((note) => String(note._id)))
  );

  return (
    <aside className="@container absolute bottom-3 right-3 top-24 z-40 flex w-[320px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-xl border-2 bg-background/95 shadow-2xl backdrop-blur">
      {/* Sized by the panel, not the viewport — query the container. */}
      <div className="flex items-start justify-between gap-2 border-b bg-muted/40 px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-bold">Narrative Notes</p>
          <p className="text-[11px] text-muted-foreground">Attached to selected or reviewed changes</p>
        </div>
        <IconButton
          label="Collapse narrative notes"
          icon={PanelRightClose}
          onClick={() => onOpenChange(false)}
        />
      </div>
      <div className="border-b px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Current Selection</p>
            <p className="text-xs">{selectedCount} change{selectedCount === 1 ? "" : "s"} selected</p>
          </div>
          {canEdit ? (
            <Button type="button" size="compact" disabled={selectedCount === 0} onClick={onAddNote}>
              <MessageSquare />
              Add
            </Button>
          ) : null}
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {notes.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed p-3 text-xs text-muted-foreground">
            No narrative notes yet. Select one or more annotations, then add a note.
          </div>
        ) : null}
        {notes.map((note) => {
          const relatedChanges = note.changeIds.map((id) => changesById.get(String(id))).filter((change): change is ChangeItem => !!change);
          const selected = selectedNoteIds.has(String(note._id));
          const isEditing = editingNoteId === String(note._id);
          return (
            <div
              key={note._id}
              className={cn(
                "rounded-lg border-2 bg-card p-2 text-xs shadow-sm transition-colors",
                selected ? "border-primary bg-primary/5" : "border-border/80"
              )}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {/* Selection highlight, not a status. */}
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <Badge variant={selected ? "default" : "outline"} size="sm">
                    {note.label}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">{relatedChanges.length} linked</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <IconButton
                    label="Zoom to note annotations"
                    icon={Search}
                    size="xs"
                    disabled={relatedChanges.length === 0 || isSavingEdit}
                    onClick={() => onFocusNote(note)}
                  />
                  {canEdit ? (
                    <IconButton
                      label="Edit narrative note"
                      icon={Edit}
                      size="xs"
                      disabled={isSavingEdit}
                      onClick={() => startEditing(note)}
                    />
                  ) : null}
                </div>
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`edit-note-trade-${note._id}`}
                      className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      Trade/Label
                    </Label>
                    <CompactTradeSelector
                      id={`edit-note-trade-${note._id}`}
                      value={editingLabel}
                      onChange={setEditingLabel}
                      disabled={isSavingEdit}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`edit-note-body-${note._id}`}
                      className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      Description
                    </Label>
                    <Textarea
                      id={`edit-note-body-${note._id}`}
                      value={editingBody}
                      onChange={(event) => setEditingBody(event.target.value)}
                      className="min-h-20 resize-none px-2 py-1.5 text-xs"
                      disabled={isSavingEdit}
                    />
                  </div>
                  <div className="flex justify-end gap-1.5">
                    <Button type="button" variant="outline" size="xs" disabled={isSavingEdit} onClick={() => setEditingNoteId(null)}>
                      Cancel
                    </Button>
                    <Button type="button" size="xs" disabled={!editingBody.trim() || isSavingEdit} onClick={() => void saveEditing(note)}>
                      <Save />
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-xs leading-snug">{note.body}</p>
              )}
              {relatedChanges.length > 0 ? (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {relatedChanges.slice(0, 3).map((change) => `Page ${change.pageNumber} · Change ${change.changeOrder}`).join(" • ")}
                  {relatedChanges.length > 3 ? ` • +${relatedChanges.length - 3} more` : ""}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function storageArgsForDocument(document: ReviewDocument | null | undefined) {
  if (document?.fileStorageId) return { storageId: document.fileStorageId };
  if (document?.fileStorageKey) return { fileKey: document.fileStorageKey };
  return "skip" as const;
}

function buildSource(document: ReviewDocument | null | undefined, fileUrl: string | null | undefined): DocumentSource | null {
  if (!document || !fileUrl) return null;
  return {
    revisionId: String(document._id),
    fileUrl,
    filename: document.originalFilename ?? "RevPilot document.pdf",
    mimeType: "application/pdf",
    mediaKind: getDocumentMediaKind(document.originalFilename ?? "RevPilot document.pdf", "application/pdf"),
  };
}

function rectsIntersect(a: ViewportRect, b: ViewportRect) {
  return a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;
}

function clampRectToPage(rect: ViewportRect): ViewportRect {
  const minSize = 0.005;
  const x = Math.max(0, Math.min(rect.x, 1 - minSize));
  const y = Math.max(0, Math.min(rect.y, 1 - minSize));
  const width = Math.max(minSize, Math.min(rect.width, 1 - x));
  const height = Math.max(minSize, Math.min(rect.height, 1 - y));
  return { x, y, width, height };
}

function resizeNormalizedRect(rect: ViewportRect, point: { x: number; y: number }, handle: ResizeHandlePosition) {
  const left = handle === "nw" || handle === "sw" ? point.x : rect.x;
  const right = handle === "ne" || handle === "se" ? point.x : rect.x + rect.width;
  const top = handle === "nw" || handle === "ne" ? point.y : rect.y;
  const bottom = handle === "sw" || handle === "se" ? point.y : rect.y + rect.height;
  return clampRectToPage({
    x: Math.min(left, right),
    y: Math.min(top, bottom),
    width: Math.abs(right - left),
    height: Math.abs(bottom - top),
  });
}

function moveNormalizedRect(rect: ViewportRect, topLeft: { x: number; y: number }): ViewportRect {
  const width = Math.min(rect.width, 1);
  const height = Math.min(rect.height, 1);
  return {
    x: Math.max(0, Math.min(topLeft.x, 1 - width)),
    y: Math.max(0, Math.min(topLeft.y, 1 - height)),
    width,
    height,
  };
}

function AddChangeNoteDialog({
  open,
  selectedCount,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  selectedCount: number;
  onOpenChange: (open: boolean) => void;
  onSubmit: (body: string, label: string) => Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [label, setLabel] = useState("General");
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit() {
    setIsSaving(true);
    try {
      await onSubmit(body, label);
      setBody("");
      setLabel("General");
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add note to {selectedCount} change{selectedCount === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="add-change-note-trade" className="text-xs font-medium">
              Trade/Label
            </Label>
            <CompactTradeSelector
              id="add-change-note-trade"
              value={label}
              onChange={setLabel}
              disabled={isSaving}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-change-note-body" className="text-xs font-medium">
              Description
            </Label>
            <Textarea
              id="add-change-note-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className="min-h-28"
              placeholder="Describe the trade impact or coordination note..."
              disabled={isSaving}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={!body.trim() || isSaving}>
            {isSaving ? <Loader2 className="size-4 animate-spin" /> : null}
            Save Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeOverlayStage({
  changes,
  notesByChangeId,
  selectedIds,
  regularizedIds,
  regularizedRects,
  hoveredId,
  onHoverChange,
  onSelectionChange,
  onSelectionMenuChange,
  onRegularizedRectChange,
}: {
  changes: ChangeItem[];
  notesByChangeId: Map<string, ChangeNote[]>;
  selectedIds: Set<string>;
  regularizedIds: Set<string>;
  regularizedRects: RegularizedRects;
  hoveredId: string | null;
  onHoverChange: (id: string | null) => void;
  onSelectionChange: (ids: Set<string>) => void;
  onSelectionMenuChange: (point: { x: number; y: number } | null) => void;
  onRegularizedRectChange: (id: string, rect: ViewportRect, persist: boolean) => void;
}) {
  // Regularized boxes only move or resize for editors; reviewers select and inspect.
  const canEdit = useCanEdit();
  const viewport = useDocumentViewport();
  const [dragRect, setDragRect] = useState<ViewportRect | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const visibleChanges = changes.filter((change) => change.pageNumber === viewport.activePage);

  const getPointer = useCallback((event: KonvaEventObject<MouseEvent>) => {
    const stage = event.target.getStage();
    return stage?.getPointerPosition() ?? null;
  }, []);

  const handleShapeClick = useCallback(
    (event: KonvaEventObject<MouseEvent>, changeId: string) => {
      event.cancelBubble = true;
      const next = new Set(event.evt.shiftKey ? selectedIds : []);
      if (event.evt.shiftKey && next.has(changeId)) {
        next.delete(changeId);
      } else {
        next.add(changeId);
      }
      onSelectionChange(next);
      onSelectionMenuChange(next.size > 0 ? getPointer(event) : null);
    },
    [getPointer, onSelectionChange, onSelectionMenuChange, selectedIds]
  );

  const getChangeRect = useCallback(
    (change: ChangeItem) => regularizedRects[String(change._id)] ?? change.regularizedBbox ?? change.normalizedBbox,
    [regularizedRects]
  );

  const handleResizeHandleDragMove = useCallback(
    (event: KonvaEventObject<DragEvent>, change: ChangeItem, handle: ResizeHandlePosition) => {
      event.cancelBubble = true;
      const pointer = getPointer(event);
      const pagePoint = pointer ? viewport.transforms.screenToPagePoint(pointer) : null;
      if (!pagePoint) return;
      onRegularizedRectChange(String(change._id), resizeNormalizedRect(getChangeRect(change), pagePoint, handle), false);
    },
    [getChangeRect, getPointer, onRegularizedRectChange, viewport.transforms]
  );

  const handleResizeHandleDragEnd = useCallback(
    (event: KonvaEventObject<DragEvent>, change: ChangeItem, handle: ResizeHandlePosition) => {
      event.cancelBubble = true;
      const pointer = getPointer(event);
      const pagePoint = pointer ? viewport.transforms.screenToPagePoint(pointer) : null;
      if (!pagePoint) return;
      onRegularizedRectChange(String(change._id), resizeNormalizedRect(getChangeRect(change), pagePoint, handle), true);
    },
    [getChangeRect, getPointer, onRegularizedRectChange, viewport.transforms]
  );

  const handleRegularizedRectDrag = useCallback(
    (event: KonvaEventObject<DragEvent>, change: ChangeItem, persist: boolean) => {
      event.cancelBubble = true;
      const topLeft = viewport.transforms.screenToPagePoint({ x: event.target.x(), y: event.target.y() });
      if (!topLeft) return;
      onRegularizedRectChange(String(change._id), moveNormalizedRect(getChangeRect(change), topLeft), persist);
    },
    [getChangeRect, onRegularizedRectChange, viewport.transforms]
  );

  return (
    <Stage
      width={viewport.workspaceSize.width}
      height={viewport.workspaceSize.height}
      className="absolute inset-0 touch-none cursor-crosshair"
      onMouseDown={(event) => {
        const pointer = getPointer(event);
        if (!pointer || event.target !== event.target.getStage()) return;
        onSelectionMenuChange(null);
        setDragStart(pointer);
        setDragRect({ x: pointer.x, y: pointer.y, width: 0, height: 0 });
      }}
      onMouseMove={(event) => {
        const pointer = getPointer(event);
        if (!pointer) return;
        if (!dragStart) return;
        setDragRect({
          x: Math.min(dragStart.x, pointer.x),
          y: Math.min(dragStart.y, pointer.y),
          width: Math.abs(pointer.x - dragStart.x),
          height: Math.abs(pointer.y - dragStart.y),
        });
      }}
      onMouseUp={(event) => {
        if (dragRect && dragRect.width > 4 && dragRect.height > 4) {
          const next = new Set(selectedIds);
          for (const change of visibleChanges) {
            if (rectsIntersect(dragRect, viewport.transforms.pageRectToScreenRect(getChangeRect(change)))) {
              next.add(String(change._id));
            }
          }
          onSelectionChange(next);
          onSelectionMenuChange(
            next.size > 0
              ? {
                  x: dragRect.x + dragRect.width / 2,
                  y: dragRect.y + dragRect.height / 2,
                }
              : null
          );
        } else if (!event.evt.shiftKey && event.target === event.target.getStage()) {
          onSelectionChange(new Set());
          onSelectionMenuChange(null);
        }
        setDragRect(null);
        setDragStart(null);
      }}
    >
      <Layer>
        {visibleChanges.map((change) => {
          const changeId = String(change._id);
          const selected = selectedIds.has(changeId);
          const hovered = hoveredId === String(change._id);
          const isRegularized = regularizedIds.has(changeId);
          const normalizedRect = isRegularized ? getChangeRect(change) : change.normalizedBbox;
          const rect = viewport.transforms.pageRectToScreenRect(normalizedRect);
          const stroke = selected ? "#111827" : change.included ? "#f59e0b" : "#ef4444";
          const fill = change.included ? "rgba(245,158,11,0.22)" : "rgba(239,68,68,0.16)";
          const points = isRegularized ? undefined : change.contourPoints?.flatMap((point) => {
            const screen = viewport.transforms.pageToScreenPoint({ x: point[0] ?? 0, y: point[1] ?? 0 });
            return [screen.x, screen.y];
          });
          const common = {
            stroke,
            fill,
            strokeWidth: selected || hovered ? 3 : 2,
            onClick: (event: KonvaEventObject<MouseEvent>) => handleShapeClick(event, String(change._id)),
            onMouseEnter: () => onHoverChange(String(change._id)),
            onMouseLeave: () => onHoverChange(null),
          };
          const regularizedDragProps = canEdit && isRegularized && selected
            ? {
                draggable: true,
                onDragStart: (event: KonvaEventObject<DragEvent>) => {
                  event.cancelBubble = true;
                  onSelectionMenuChange(null);
                },
                onDragMove: (event: KonvaEventObject<DragEvent>) => handleRegularizedRectDrag(event, change, false),
                onDragEnd: (event: KonvaEventObject<DragEvent>) => handleRegularizedRectDrag(event, change, true),
              }
            : {};
          return points && points.length >= 6 ? (
            <Line key={change._id} points={points} closed {...common} />
          ) : (
            <Rect key={change._id} x={rect.x} y={rect.y} width={rect.width} height={rect.height} {...common} {...regularizedDragProps} />
          );
        })}
        {visibleChanges.map((change) => {
          const changeId = String(change._id);
          if (!canEdit || !regularizedIds.has(changeId) || !selectedIds.has(changeId)) return null;
          const rect = viewport.transforms.pageRectToScreenRect(getChangeRect(change));
          const handles: Array<{ id: ResizeHandlePosition; x: number; y: number }> = [
            { id: "nw", x: rect.x, y: rect.y },
            { id: "ne", x: rect.x + rect.width, y: rect.y },
            { id: "sw", x: rect.x, y: rect.y + rect.height },
            { id: "se", x: rect.x + rect.width, y: rect.y + rect.height },
          ];
          return handles.map((handle) => (
            <Rect
              key={`${changeId}-${handle.id}`}
              x={handle.x - 5}
              y={handle.y - 5}
              width={10}
              height={10}
              fill="#2563eb"
              stroke="#ffffff"
              strokeWidth={1.5}
              draggable
              onDragMove={(event) => handleResizeHandleDragMove(event, change, handle.id)}
              onDragEnd={(event) => handleResizeHandleDragEnd(event, change, handle.id)}
              onMouseDown={(event) => {
                event.cancelBubble = true;
                onSelectionMenuChange(null);
              }}
            />
          ));
        })}
        {visibleChanges.map((change) => {
          if (!notesByChangeId.has(String(change._id))) return null;
          const rect = viewport.transforms.pageRectToScreenRect(getChangeRect(change));
          return (
            <Rect
              key={`${change._id}-note`}
              x={rect.x + rect.width - 10}
              y={rect.y - 10}
              width={10}
              height={10}
              fill="#2563eb"
              stroke="#ffffff"
              strokeWidth={1}
            />
          );
        })}
        {dragRect ? (
          <Rect
            x={dragRect.x}
            y={dragRect.y}
            width={dragRect.width}
            height={dragRect.height}
            fill="rgba(37,99,235,0.12)"
            stroke="#2563eb"
            dash={[6, 4]}
            strokeWidth={1}
          />
        ) : null}
      </Layer>
    </Stage>
  );
}

function ChangeFocusController({
  focusRequest,
  onCameraChange,
}: {
  focusRequest: NoteFocusRequest | null;
  onCameraChange?: Dispatch<SetStateAction<ViewportCamera>>;
}) {
  const viewport = useDocumentViewport();
  const { activePage, activePageMetrics, viewportPadding, workspaceSize } = viewport;
  const handledFocusNonceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!focusRequest || !onCameraChange || activePage !== focusRequest.pageNumber || focusRequest.regions.length === 0) {
      return;
    }
    if (handledFocusNonceRef.current === focusRequest.nonce) {
      return;
    }
    handledFocusNonceRef.current = focusRequest.nonce;

    const union = focusRequest.regions.reduce<ViewportRect>((acc, rect) => {
      const minX = Math.min(acc.x, rect.x);
      const minY = Math.min(acc.y, rect.y);
      const maxX = Math.max(acc.x + acc.width, rect.x + rect.width);
      const maxY = Math.max(acc.y + acc.height, rect.y + rect.height);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }, focusRequest.regions[0]);

    const centerX = union.x + union.width / 2;
    const centerY = union.y + union.height / 2;
    const regionSize = Math.max(union.width, union.height, 0.05);
    const zoom = Math.min(5, Math.max(2.2, 0.45 / regionSize));
    const contentWidth = Math.max(
      1,
      workspaceSize.width - viewportPadding.left - viewportPadding.right
    );
    const aspectRatio = activePageMetrics?.aspectRatio ?? 1.414;
    const pan = {
      x: (0.5 - centerX) * contentWidth * zoom,
      y: (0.5 - centerY) * contentWidth * aspectRatio * zoom,
    };

    onCameraChange((current) => ({
      ...current,
      zoom,
      pan: clampPanToDocumentBounds({
        workspaceSize,
        viewportPadding,
        pageMetrics: activePageMetrics,
        zoom,
        pan,
      }),
      fitMode: "custom",
      renderScale: getRenderScaleForZoom(zoom),
    }));
  }, [activePage, activePageMetrics, focusRequest, onCameraChange, viewportPadding, workspaceSize]);

  return null;
}

function ReviewViewport({
  source,
  activePage,
  onActivePageChange,
  changes,
  notesByChangeId,
  selectedIds,
  regularizedIds,
  regularizedRects,
  hoveredId,
  onHoverChange,
  onSelectionChange,
  title,
  showOverlay,
  camera,
  onCameraChange,
  selectedChanges,
  focusRequest,
  onExcludeSelected,
  onIncludeSelected,
  onAddNote,
  onRegularizeSelected,
  onRegularizedRectChange,
  selectionActionsDisabled,
}: {
  source: DocumentSource | null;
  activePage: number;
  onActivePageChange: (page: number) => void;
  changes: ChangeItem[];
  notesByChangeId: Map<string, ChangeNote[]>;
  selectedIds: Set<string>;
  regularizedIds: Set<string>;
  regularizedRects: RegularizedRects;
  hoveredId: string | null;
  onHoverChange: (id: string | null) => void;
  onSelectionChange: (ids: Set<string>) => void;
  title: string;
  showOverlay: boolean;
  camera?: ViewportCamera;
  onCameraChange?: Dispatch<SetStateAction<ViewportCamera>>;
  selectedChanges: ChangeItem[];
  focusRequest?: NoteFocusRequest | null;
  onExcludeSelected: () => void;
  onIncludeSelected: () => void;
  onAddNote: () => void;
  onRegularizeSelected: () => void;
  onRegularizedRectChange: (id: string, rect: ViewportRect, persist: boolean) => void;
  selectionActionsDisabled?: boolean;
}) {
  const [pageCount, setPageCount] = useState(1);
  const [selectionMenuPoint, setSelectionMenuPoint] = useState<{ x: number; y: number } | null>(null);
  const spacePanActive = useSpacePanMode();
  const pointerOwner = showOverlay && !spacePanActive ? "tool" : "viewport";
  const { selectedIncludedCount, selectedExcludedCount } = useMemo(() => {
    const includedCount = selectedChanges.filter((change) => change.included).length;
    return {
      selectedIncludedCount: includedCount,
      selectedExcludedCount: selectedChanges.length - includedCount,
    };
  }, [selectedChanges]);

  if (!source) {
    return <Card className="flex min-h-[480px] items-center justify-center text-sm text-muted-foreground">PDF unavailable.</Card>;
  }
  return (
    <div className="relative h-full min-h-0 overflow-hidden border-r-2 border-border bg-background last:border-r-0">
      <DocumentViewportHost
        source={source}
        activePage={activePage}
        onActivePageChange={onActivePageChange}
        onPageCountChange={setPageCount}
        camera={camera}
        onCameraChange={onCameraChange}
        pointerOwner={pointerOwner}
        enableWheelZoom
        // Side-by-side panes share a mirrored camera; independent sidebar
        // padding would desync framing when only one pane is collapsed.
        pageSidebar={false}
        viewportPadding={{ top: 88, right: 24, bottom: 24, left: 24 }}
        className="absolute inset-0 overflow-hidden"
        toolbar={
          <DocumentViewportToolbar>
            <DocumentViewportToolbarSection className="min-w-0 flex-1 overflow-hidden">
              <FileText className="size-4 shrink-0" />
              <span className="truncate text-xs font-semibold">{title}</span>
            </DocumentViewportToolbarSection>
            <DocumentViewportPageControls activePage={activePage} pageCount={pageCount} />
          </DocumentViewportToolbar>
        }
      >
        {showOverlay ? (
          <>
            <ChangeFocusController focusRequest={focusRequest ?? null} onCameraChange={onCameraChange} />
            <ChangeOverlayStage
              changes={changes}
              notesByChangeId={notesByChangeId}
              selectedIds={selectedIds}
              regularizedIds={regularizedIds}
              regularizedRects={regularizedRects}
              hoveredId={hoveredId}
              onHoverChange={onHoverChange}
              onSelectionChange={onSelectionChange}
              onSelectionMenuChange={setSelectionMenuPoint}
              onRegularizedRectChange={onRegularizedRectChange}
            />
            <ChangeSelectionMenu
              point={selectionMenuPoint}
              selectedCount={selectedIds.size}
              showExclude={selectedIncludedCount > 0}
              showInclude={selectedExcludedCount > 0}
              disabled={selectionActionsDisabled}
              onExclude={onExcludeSelected}
              onInclude={onIncludeSelected}
              onAddNote={onAddNote}
              onRegularize={onRegularizeSelected}
              onClose={() => setSelectionMenuPoint(null)}
            />
          </>
        ) : null}
      </DocumentViewportHost>
    </div>
  );
}

export function PdfChangeReviewView({
  comparisonId,
  details,
  completedMode = false,
  onBackToSummary,
}: {
  comparisonId: Id<"revPilotScheduleComparisons">;
  details: ComparisonDetails;
  completedMode?: boolean;
  onBackToSummary?: () => void;
}) {
  const exitViewer = useViewerExit({ fallback: { to: "/revPilot", title: "RevPilot" } });
  const [mode, setMode] = useState<ReviewMode>("single");
  const [showExcluded, setShowExcluded] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [selectedDocumentPairId, setSelectedDocumentPairId] = useState(() => {
    const firstPairId = details.documentPairs?.[0]?._id;
    return firstPairId ? String(firstPairId) : "all";
  });
  const [activePage, setActivePage] = useState(1);
  const [mirroredCamera, setMirroredCamera] = useState<ViewportCamera>(DEFAULT_REVIEW_CAMERA);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [regularizedIds, setRegularizedIds] = useState<Set<string>>(() => new Set());
  const [regularizedRects, setRegularizedRects] = useState<RegularizedRects>({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState<NoteFocusRequest | null>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  useOnEditAccessLost(() => setNoteDialogOpen(false));
  const [isUpdatingSelection, setIsUpdatingSelection] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const documentPairs = details.documentPairs ?? [];
  const selectedPair = documentPairs.find((pair) => String(pair._id) === selectedDocumentPairId) ?? null;
  const v1Document = selectedPair?.v1Document ?? details.v1Document;
  const v2Document = selectedPair?.v2Document ?? details.v2Document;

  const { data: v1Url } = useQuery(convexQuery(api.scheduleComparisons.getStorageUrl, storageArgsForDocument(v1Document)));
  const { data: v2Url } = useQuery(convexQuery(api.scheduleComparisons.getStorageUrl, storageArgsForDocument(v2Document)));
  const { data: changes } = useQuery(convexQuery(api.revPilotChanges.listByComparison, {
    comparisonId,
    ...(selectedDocumentPairId !== "all" ? { documentPairId: selectedDocumentPairId as Id<"revPilotDocumentPairs"> } : {}),
  }));
  const { data: notes } = useQuery(convexQuery(api.revPilotChanges.listNotesByComparison, { comparisonId }));
  const { data: summary } = useQuery(convexQuery(api.revPilotChanges.getSelectionsSummary, { comparisonId }));

  const updateSelections = useMutation(api.revPilotChanges.updateManyChangeSelections);
  const updateRegularizedBbox = useMutation(api.revPilotChanges.updateRegularizedBbox);
  const confirmSelections = useMutation(api.revPilotChanges.confirmSelections);
  const createNote = useMutation(api.revPilotChanges.createNote);
  const updateNote = useMutation(api.revPilotChanges.updateNote);

  const v1Source = buildSource(v1Document, v1Url);
  const v2Source = buildSource(v2Document, v2Url);
  const typedChanges = useMemo(() => (changes ?? []) as ChangeItem[], [changes]);
  const typedNotes = useMemo(() => (notes ?? []) as ChangeNote[], [notes]);
  const visibleChanges = showExcluded ? typedChanges : typedChanges.filter((change) => change.included);
  const selectedChanges = useMemo(
    () => typedChanges.filter((change) => selectedIds.has(String(change._id))),
    [selectedIds, typedChanges]
  );

  const resetRegularizedToSaved = useCallback(() => {
    const nextIds = new Set<string>();
    const nextRects: RegularizedRects = {};
    for (const change of typedChanges) {
      if (!change.regularizedBbox) continue;
      const id = String(change._id);
      nextIds.add(id);
      nextRects[id] = change.regularizedBbox;
    }
    setRegularizedIds(nextIds);
    setRegularizedRects(nextRects);
  }, [typedChanges]);

  useEffect(() => {
    resetRegularizedToSaved();
  }, [resetRegularizedToSaved]);
  // A box moved but not saved before a demotion snaps back to its saved position.
  useOnEditAccessLost(resetRegularizedToSaved);

  const changesById = useMemo(() => {
    const map = new Map<string, ChangeItem>();
    for (const change of typedChanges) map.set(String(change._id), change);
    return map;
  }, [typedChanges]);

  const activeNotes = useMemo(
    () => typedNotes.filter((note) => note.changeIds.some((id) => changesById.has(String(id)))),
    [changesById, typedNotes]
  );

  const notesByChangeId = useMemo(() => {
    const map = new Map<string, ChangeNote[]>();
    for (const note of activeNotes) {
      for (const changeId of note.changeIds) {
        const key = String(changeId);
        map.set(key, [...(map.get(key) ?? []), note]);
      }
    }
    return map;
  }, [activeNotes]);

  async function handleBatchSelection(included: boolean) {
    if (selectedIds.size === 0) return;
    setIsUpdatingSelection(true);
    try {
      await updateSelections({
        comparisonId,
        changeIds: Array.from(selectedIds) as Id<"revPilotChanges">[],
        included,
      });
      toast.success(included ? "Selected changes included" : "Selected changes excluded");
      setSelectedIds(new Set());
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to update selections");
    } finally {
      setIsUpdatingSelection(false);
    }
  }

  function handleDocumentPairChange(value: string) {
    setSelectedDocumentPairId(value);
    setSelectedIds(new Set());
    setRegularizedIds(new Set());
    setRegularizedRects({});
    setActivePage(1);
    setMirroredCamera(DEFAULT_REVIEW_CAMERA);
  }

  function handleShowExcludedChange(value: boolean) {
    setShowExcluded(value);
    if (!value) {
      setSelectedIds((current) => {
        const next = new Set<string>();
        for (const id of current) {
          if (changesById.get(id)?.included) next.add(id);
        }
        return next;
      });
    }
  }

  async function handleRegularizeSelected() {
    if (selectedIds.size === 0) return;
    const selectedRects: Array<{ id: string; rect: ViewportRect }> = [];
    for (const id of selectedIds) {
      const change = changesById.get(id);
      if (!change) continue;
      selectedRects.push({ id, rect: regularizedRects[id] ?? change.regularizedBbox ?? change.normalizedBbox });
    }
    setRegularizedIds((current) => new Set([...current, ...selectedIds]));
    setRegularizedRects((current) => {
      const next = { ...current };
      for (const { id, rect } of selectedRects) {
        if (!next[id]) next[id] = rect;
      }
      return next;
    });
    try {
      await Promise.all(
        selectedRects.map(({ id, rect }) =>
          updateRegularizedBbox({ changeId: id as Id<"revPilotChanges">, regularizedBbox: rect })
        )
      );
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to save regularized shape");
    }
  }

  function handleRegularizedRectChange(id: string, rect: ViewportRect, persist: boolean) {
    setRegularizedIds((current) => new Set(current).add(id));
    setRegularizedRects((current) => ({ ...current, [id]: rect }));
    if (!persist) return;
    void updateRegularizedBbox({ changeId: id as Id<"revPilotChanges">, regularizedBbox: rect }).catch((error) => {
      notifyError(error, error instanceof Error ? error.message : "Failed to save regularized shape");
    });
  }

  async function handleCreateNote(body: string, label: string) {
    try {
      await createNote({
        comparisonId,
        changeIds: Array.from(selectedIds) as Id<"revPilotChanges">[],
        body,
        label,
      });
      toast.success("Note added");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to add note");
      throw error;
    }
  }

  async function handleCompleteReview() {
    setIsConfirming(true);
    try {
      await confirmSelections({ comparisonId });
      toast.success("Review completed");
      if (completedMode) onBackToSummary?.();
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to complete review");
    } finally {
      setIsConfirming(false);
    }
  }

  async function handleUpdateNote(noteId: Id<"revPilotChangeNotes">, body: string, label: string) {
    await updateNote({ noteId, body, label });
    toast.success("Note updated");
  }

  function handleFocusNote(note: ChangeNote) {
    const relatedChanges = note.changeIds
      .map((id) => changesById.get(String(id)))
      .filter((change): change is ChangeItem => !!change);
    const firstChange = relatedChanges[0];
    if (!firstChange) return;

    setSelectedIds(new Set(relatedChanges.map((change) => String(change._id))));
    setHoveredId(String(firstChange._id));
    if (relatedChanges.some((change) => !change.included)) {
      setShowExcluded(true);
    }
    setActivePage(firstChange.pageNumber);
    setFocusRequest({
      nonce: Date.now(),
      pageNumber: firstChange.pageNumber,
      regions: relatedChanges
        .filter((change) => change.pageNumber === firstChange.pageNumber)
        .map((change) => regularizedRects[String(change._id)] ?? change.regularizedBbox ?? change.normalizedBbox),
    });
  }

  function handleBack() {
    // Completed reviews return to the comparison summary; an active review has no summary yet.
    if (completedMode && onBackToSummary) {
      onBackToSummary();
      return;
    }
    exitViewer();
  }

  if (changes === undefined || notes === undefined || summary === undefined) {
    return <ProcessingSpinner message="Loading PDF change review..." />;
  }

  const workspaceHeader = (
    <ReviewWorkspaceControls
      comparisonName={details.comparison.name}
      documentPairs={documentPairs}
      selectedDocumentPairId={selectedDocumentPairId}
      onDocumentPairChange={handleDocumentPairChange}
      showExcluded={showExcluded}
      onShowExcludedChange={handleShowExcludedChange}
      mode={mode}
      onModeChange={setMode}
      commentsOpen={commentsOpen}
      onCommentsOpenChange={setCommentsOpen}
      selectedCount={selectedIds.size}
      completedMode={completedMode}
      isCompleting={isConfirming}
      onBack={handleBack}
      onCompleteReview={() => void handleCompleteReview()}
    />
  );

  const commonViewportProps = {
    activePage,
    onActivePageChange: setActivePage,
    notesByChangeId,
    selectedIds,
    selectedChanges,
    regularizedIds,
    regularizedRects,
    hoveredId,
    onHoverChange: setHoveredId,
    onSelectionChange: setSelectedIds,
    focusRequest,
    onExcludeSelected: () => void handleBatchSelection(false),
    onIncludeSelected: () => void handleBatchSelection(true),
    onAddNote: () => setNoteDialogOpen(true),
    onRegularizeSelected: handleRegularizeSelected,
    onRegularizedRectChange: handleRegularizedRectChange,
    selectionActionsDisabled: isUpdatingSelection,
  };

  return (
    <div
      // Grows into the page-content flex column; -m-6/lg:-m-8 cancel its padding.
      className="relative -m-6 min-h-[720px] flex-1 overflow-hidden bg-background lg:-m-8"
    >
      {workspaceHeader}
      <div className={cn("absolute inset-x-0 bottom-0 top-12 grid", mode === "side-by-side" ? "gap-1 bg-border xl:grid-cols-2" : "grid-cols-1") }>
        {mode === "side-by-side" ? (
          <>
            <ReviewViewport
              source={v1Source}
              {...commonViewportProps}
              changes={[]}
              title="Before (V1)"
              showOverlay={false}
              camera={mirroredCamera}
              onCameraChange={setMirroredCamera}
            />
            <ReviewViewport
              source={v2Source}
              {...commonViewportProps}
              changes={visibleChanges}
              title="After (V2)"
              showOverlay
              camera={mirroredCamera}
              onCameraChange={setMirroredCamera}
            />
          </>
        ) : (
          <ReviewViewport
            source={v2Source}
            {...commonViewportProps}
            changes={visibleChanges}
            title="After (V2) raw PDF"
            showOverlay
            camera={mirroredCamera}
            onCameraChange={setMirroredCamera}
          />
        )}
      </div>
      <CommentsSideDrawer
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        notes={activeNotes}
        selectedChanges={selectedChanges}
        changesById={changesById}
        selectedCount={selectedIds.size}
        onAddNote={() => setNoteDialogOpen(true)}
        onFocusNote={handleFocusNote}
        onUpdateNote={handleUpdateNote}
      />

      <AddChangeNoteDialog
        open={noteDialogOpen}
        selectedCount={selectedIds.size}
        onOpenChange={setNoteDialogOpen}
        onSubmit={handleCreateNote}
      />
    </div>
  );
}
